import { useCallback, useLayoutEffect } from "react";
import { create } from "zustand";
import { useAuth } from "@/hooks/use-auth";
import { useAuthStore } from "@/stores/use-auth-store";
import { currentTenantId } from "@/lib/command-center-badges";
import { APP_CONSTANTS } from "@/config";

export type TeamWorkspaceView = "chat" | "summaries";
export type TeamWorkspaceScroll = { top: number; followLatest: boolean; anchorId?: string; anchorOffset?: number; newestId?: string; resumeRequired?: boolean };
type Session = {
  scope: string | null;
  active: boolean;
  draft: string;
  view: TeamWorkspaceView;
  returnTo: string;
  workspaceTo: string;
  scroll: TeamWorkspaceScroll | null;
  expandedGroups: string[];
};
const empty: Session = { scope: null, active: false, draft: "", view: "chat", returnTo: "/", workspaceTo: "/centro-de-comando", scroll: null, expandedGroups: [] };
let invalidatedRevision: number | null = null;

// Deliberately memory-only. An outgoing identity can never overwrite its successor.
export const useTeamWorkspaceStore = create<Session>(() => ({ ...empty }));
export function resetTeamWorkspace() { useTeamWorkspaceStore.setState({ ...empty }); }
export function updateTeamWorkspace(scope: string | null, changes: Partial<Omit<Session, "scope">>) {
  if (invalidatedRevision === null && scope && useTeamWorkspaceStore.getState().scope === scope) useTeamWorkspaceStore.setState(changes);
}
useAuthStore.subscribe((next, previous) => {
  if (previous.user && (next.sessionRevision !== previous.sessionRevision || next.user?.id !== previous.user.id || next.user?.tenant_id !== previous.user.tenant_id)) invalidatedRevision = next.sessionRevision;
  if (invalidatedRevision !== null) {
    resetTeamWorkspace();
    return;
  }
  if (next.sessionRevision !== previous.sessionRevision || next.user?.id !== previous.user?.id || next.user?.tenant_id !== previous.user?.tenant_id) resetTeamWorkspace();
});

// Observe even while minimized/unmounted. Only a document reload can release
// the block: reconciling auth does not remove outgoing-tenant query caches.
if (typeof window !== "undefined") {
  const identityKeys = new Set<string>([
    APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY,
    APP_CONSTANTS.AUTH_ACCESS_TOKEN_LOCAL_STORAGE_KEY,
    APP_CONSTANTS.AUTH_USER_LOCAL_STORAGE_KEY,
    APP_CONSTANTS.AUTH_LOCAL_STORAGE_KEY,
  ]);
  window.addEventListener("storage", (event) => {
    if ((event.storageArea && event.storageArea !== window.localStorage)
      || (event.key !== null && !identityKeys.has(event.key))) return;
    invalidatedRevision = useAuthStore.getState().sessionRevision;
    resetTeamWorkspace();
  });
}

export function useTeamWorkspaceSession() {
  const { user, sessionRevision } = useAuth();
  const tenant = user?.tenant_id ?? currentTenantId();
  const scope = invalidatedRevision === null && user?.id && tenant ? `${tenant}:${user.id}:${sessionRevision ?? 0}` : null;
  const stored = useTeamWorkspaceStore();
  useLayoutEffect(() => {
    if (invalidatedRevision !== null) return;
    if (useTeamWorkspaceStore.getState().scope !== scope) useTeamWorkspaceStore.setState({ ...empty, scope });
  }, [scope]);
  const update = useCallback((changes: Partial<Omit<Session, "scope">>) => updateTeamWorkspace(scope, changes), [scope]);
  return { ...(stored.scope === scope && scope ? stored : empty), scope, blocked: invalidatedRevision !== null,
    update };
}
